import { useMemo } from "react";
import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import FactCheckRoundedIcon from "@mui/icons-material/FactCheckRounded";
import ForumRoundedIcon from "@mui/icons-material/ForumRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import PointOfSaleRoundedIcon from "@mui/icons-material/PointOfSaleRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import PriceChangeRoundedIcon from "@mui/icons-material/PriceChangeRounded";
import RequestQuoteRoundedIcon from "@mui/icons-material/RequestQuoteRounded";
import QrCodeScannerRoundedIcon from "@mui/icons-material/QrCodeScannerRounded";
import ShoppingCartCheckoutRoundedIcon from "@mui/icons-material/ShoppingCartCheckoutRounded";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import WarehouseRoundedIcon from "@mui/icons-material/WarehouseRounded";
import {
  Box,
  Button,
  Chip,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "../i18n";
import { useAuth } from "../context/AuthContext";
import { useConnectivity } from "../context/ConnectivityContext";
import { titleCase } from "../utils/format";
import AppLogo from "./AppLogo";

const OFFLINE_ALLOWED_PATHS = new Set(["/products", "/inventory", "/pos"]);
const NAV_KEYS = {
  "/dashboard": "dashboard", "/products": "products", "/inventory": "inventory",
  "/purchase-invoices": "purchaseInvoices", "/payments-expenses": "paymentsExpenses",
  "/purchase-orders": "purchaseOrders",
  "/pricing": "pricing", "/pos": "pos", "/hardware": "hardware", "/sales": "sales",
  "/customers": "customers", "/conversations": "conversations",
  "/employee-operations": "teamInbox", "/quotations": "quotations", "/orders": "orders",
  "/reports": "reports", "/documents": "documents", "/team": "team",
  "/store-import": "import", "/audit-logs": "auditLogs", "/onboarding": "approvalStatus",
};
const SECTION_KEYS = {
  Overview: "overview", "Catalog & stock": "catalogStock",
  "Customers & sales": "customersSales", Knowledge: "knowledge", Administration: "administration",
};

const tenantSections = [
  {
    label: "Overview",
    links: [
      { label: "Dashboard", path: "/dashboard", icon: DashboardRoundedIcon },
    ],
  },
  {
    label: "Catalog & stock",
    links: [
      { label: "Products", path: "/products", icon: Inventory2RoundedIcon },
      { label: "Inventory", path: "/inventory", icon: WarehouseRoundedIcon },
      { label: "Purchase orders", path: "/purchase-orders", icon: ShoppingCartCheckoutRoundedIcon, roles: ["owner", "admin"] },
      { label: "Purchase invoices", path: "/purchase-invoices", icon: ReceiptLongRoundedIcon, roles: ["owner", "admin"] },
      { label: "Payments & Expenses", path: "/payments-expenses", icon: ReceiptLongRoundedIcon, roles: ["owner", "admin"] },
      { label: "Pricing & Discounts", path: "/pricing", icon: PriceChangeRoundedIcon, roles: ["owner", "admin"] },
    ],
  },
  {
    label: "Customers & sales",
    links: [
      { label: "Point of Sale", path: "/pos", icon: PointOfSaleRoundedIcon, roles: ["owner", "admin", "staff"] },
      { label: "Hardware setup", path: "/hardware", icon: QrCodeScannerRoundedIcon, roles: ["owner", "admin", "staff"] },
      { label: "Sales", path: "/sales", icon: ReceiptLongRoundedIcon, roles: ["owner", "admin", "staff"] },
      { label: "Customers", path: "/customers", icon: PeopleAltRoundedIcon },
      { label: "Conversations", path: "/conversations", icon: ForumRoundedIcon },
      {
        label: "Team inbox",
        path: "/employee-operations",
        icon: SupportAgentRoundedIcon,
        roles: ["owner", "admin", "staff"],
      },
      { label: "Quotations", path: "/quotations", icon: RequestQuoteRoundedIcon },
      { label: "Orders", path: "/orders", icon: ShoppingCartCheckoutRoundedIcon },
      { label: "Reports", path: "/reports", icon: InsightsRoundedIcon, roles: ["owner"] },
    ],
  },
  {
    label: "Knowledge",
    links: [
      { label: "Knowledge documents", path: "/documents", icon: DescriptionRoundedIcon },
    ],
  },
  {
    label: "Administration",
    links: [
      { label: "Team members", path: "/team", icon: PeopleAltRoundedIcon, roles: ["owner"] },
      { label: "Import store data", path: "/store-import", icon: UploadFileRoundedIcon, roles: ["owner", "admin"] },
      { label: "Audit logs", path: "/audit-logs", icon: FactCheckRoundedIcon, roles: ["owner", "admin"] },
    ],
  },
];

function NavigationLink({ link, active, onClick }) {
  const Icon = link.icon;
  const { t } = useTranslation();
  return (
    <ListItemButton
      component={Link}
      to={link.path}
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      sx={{
        color: active ? "#fff" : "#cbd5e1",
        borderRadius: 2.5,
        px: 1.35,
        py: 0.9,
        minHeight: 44,
        bgcolor: active ? "rgba(59,130,246,0.25)" : "transparent",
        border: active ? "1px solid rgba(96,165,250,0.28)" : "1px solid transparent",
        "&:hover": { bgcolor: "rgba(255,255,255,0.08)", color: "white" },
      }}
    >
      <ListItemIcon sx={{ minWidth: 36, color: "inherit" }}>
        <Icon fontSize="small" />
      </ListItemIcon>
      <ListItemText
        primary={NAV_KEYS[link.path] ? t(`nav.${NAV_KEYS[link.path]}`) : link.label}
        slotProps={{
          primary: {
            fontWeight: active ? 900 : 650,
            fontSize: "0.9rem",
          },
        }}
      />
      {Number(link.badge || 0) > 0 && (
        <Chip
          label={Number(link.badge) > 99 ? "99+" : Number(link.badge)}
          size="small"
          sx={{ height: 22, minWidth: 28, bgcolor: "#f59e0b", color: "#111827" }}
        />
      )}
    </ListItemButton>
  );
}

export default function Sidebar({ drawerWidth = 280, mobileOpen = false, onMobileClose }) {
  const { t } = useTranslation();
  const {
    logout,
    role,
    platformRole,
    contextReady,
    workspaceOperational,
    selectedWorkspace,
    selectedStore,
    user,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { state: connectivityState } = useConnectivity();
  const offlineLimited = connectivityState === "offline" || connectivityState === "degraded";
  const sections = useMemo(() => {
    if (!contextReady || !workspaceOperational) return [];
    return tenantSections
      .map((section) => ({
        ...section,
        links: section.links.filter((link) => (
          (!link.roles || link.roles.includes(role))
          && (!offlineLimited || OFFLINE_ALLOWED_PATHS.has(link.path))
        )),
      }))
      .filter((section) => section.links.length > 0);
  }, [contextReady, offlineLimited, role, workspaceOperational]);

  const isActive = (path) => (
    location.pathname === path || (path !== "/dashboard" && location.pathname.startsWith(`${path}/`))
  );

  const handleLogout = async () => {
    await logout();
    onMobileClose?.();
    navigate("/login", { replace: true });
  };

  const drawerContent = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflowY: "auto",
        p: 2,
        color: "white",
      }}
    >
      <Box sx={{ px: 0.5, mb: 2.75 }}>
        <AppLogo light />
      </Box>

      {contextReady && (
        <Box
          sx={{
            p: 1.55,
            mb: 2.25,
            borderRadius: 3,
            bgcolor: "rgba(255,255,255,0.065)",
            border: "1px solid rgba(255,255,255,0.09)",
          }}
        >
          <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 800 }}>
            {t("nav.currentStore")}
          </Typography>
          <Typography variant="body2" fontWeight={900} noWrap sx={{ mt: 0.45 }}>
            {selectedStore?.name || t("nav.store")}
          </Typography>
          <Typography variant="caption" sx={{ color: "#cbd5e1" }} noWrap display="block">
            {selectedWorkspace?.name || t("nav.workspace")}
          </Typography>
          <Chip
            label={t(`team.${role}`, { defaultValue: titleCase(role) })}
            size="small"
            sx={{ mt: 1.1, bgcolor: "rgba(56,189,248,0.13)", color: "#bae6fd", height: 24 }}
          />
        </Box>
      )}

      {platformRole && (
        <Box
          sx={{
            p: 1.5,
            mb: 2,
            borderRadius: 3,
            bgcolor: "rgba(245,158,11,0.09)",
            border: "1px solid rgba(245,158,11,0.22)",
          }}
        >
          <Typography variant="caption" sx={{ color: "#fde68a", fontWeight: 900 }}>
            PLATFORM ACCESS
          </Typography>
          <Typography variant="body2" fontWeight={900} sx={{ mt: 0.35 }}>
            {titleCase(platformRole)}
          </Typography>
        </Box>
      )}

      <List sx={{ display: "grid", gap: 0.35, p: 0 }}>
        {platformRole && (
          <>
            <Typography variant="overline" sx={{ color: "#64748b", px: 1.35, mt: 0.5, fontWeight: 900 }}>
              Platform
            </Typography>
            <NavigationLink
              link={{ label: "Operations", path: "/platform", icon: AdminPanelSettingsRoundedIcon }}
              active={isActive("/platform") && !location.pathname.startsWith("/platform/ai-usage")}
              onClick={onMobileClose}
            />
            <NavigationLink
              link={{ label: "AI usage & cost", path: "/platform/ai-usage", icon: InsightsRoundedIcon }}
              active={isActive("/platform/ai-usage")}
              onClick={onMobileClose}
            />
          </>
        )}

        {contextReady && !workspaceOperational && (
          <NavigationLink
            link={{ label: "Approval status", path: "/onboarding", icon: PointOfSaleRoundedIcon }}
            active={isActive("/onboarding")}
            onClick={onMobileClose}
          />
        )}

        {sections.map((section, index) => (
          <Box key={section.label} sx={{ mt: index === 0 ? 0.5 : 1.3 }}>
            <Typography
              variant="overline"
              sx={{ color: "#64748b", px: 1.35, fontWeight: 900, letterSpacing: "0.09em" }}
            >
              {t(`nav.${SECTION_KEYS[section.label]}`)}
            </Typography>
            <Box sx={{ display: "grid", gap: 0.35, mt: 0.15 }}>
              {section.links.map((link) => (
                <NavigationLink
                  key={link.path}
                  link={link}
                  active={isActive(link.path)}
                  onClick={onMobileClose}
                />
              ))}
            </Box>
          </Box>
        ))}

      </List>

      <Box sx={{ flexGrow: 1, minHeight: 20 }} />

      <Box sx={{ px: 0.75, py: 1.25, mb: 1 }}>
        <Typography variant="body2" fontWeight={850} noWrap>
          {user?.username || t("common.account")}
        </Typography>
        <Typography variant="caption" sx={{ color: "#94a3b8" }} noWrap display="block">
          {user?.email}
        </Typography>
      </Box>

      <Button
        variant="outlined"
        color="inherit"
        startIcon={<LogoutRoundedIcon />}
        onClick={handleLogout}
        sx={{
          borderColor: "rgba(255,255,255,0.2)",
          color: "#e2e8f0",
          "&:hover": { borderColor: "rgba(255,255,255,0.42)", bgcolor: "rgba(255,255,255,0.07)" },
        }}
      >
        {t("common.signOut")}
      </Button>
    </Box>
  );

  const paperStyle = {
    width: drawerWidth,
    border: "none",
    background: "linear-gradient(180deg, #0f172a 0%, #111827 56%, #020617 100%)",
  };

  return (
    <>
      {/* The RTL Emotion cache flips Drawer's physical left positioning; do not also flip anchor. */}
      <Drawer
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{ display: { xs: "block", md: "none" }, "& .MuiDrawer-paper": paperStyle }}
      >
        {drawerContent}
      </Drawer>
      <Drawer
        variant="permanent"
        anchor="left"
        open
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          display: { xs: "none", md: "block" },
          "& .MuiDrawer-paper": paperStyle,
        }}
      >
        {drawerContent}
      </Drawer>
    </>
  );
}
